import { differenceInMinutes } from 'date-fns';
import { getGuestSession } from '../utilities/guest.utilities';
import { isGuestUser$ } from '../utilities/user.utilities';

interface Token {
    accessToken: string;
    accessTokenExpiresAt: string;
}

export interface AuthHeaderResponse {
    'X-Is-ModelShareKey'?: string;
    Authorization?: string;
}

let token: Token | null = null;
let promise: Promise<string> | null = null;

export const getShareAccessToken = () => {
    const guest = getGuestSession();
    return guest?.token;
};

export const getAccessToken = (): Promise<string> => {
    // If we have a pending promise, wait for this promise to be fulfilled, rather than making duplicate calls for an access token
    if (promise) {
        return promise;
    }

    if (!token?.accessToken) {
        return requestToken();
    }

    const expiresAt = new Date(token.accessTokenExpiresAt);
    const expiresIn = differenceInMinutes(expiresAt, new Date());

    if (expiresIn < 5) {
        return requestToken();
    }

    return Promise.resolve(token.accessToken);
};

export const requestToken = (): Promise<string> => {
    const shareToken = getShareAccessToken();

    promise = (async () => {
        if (shareToken) {
            return shareToken;
        }

        try {
            const response = await fetch(`/auth/token`, { credentials: 'same-origin' });

            if (response.status !== 200) throw new Error(`Token refresh returned ${response.status}`);

            // Store the access token
            token = await response.json();

            if (!token?.accessToken) {
                throw new Error('No access token received');
            }

            return token.accessToken;
        } catch (error: unknown) {
            console.error('Failed to retrieve access token:', error);

            window.location.href = '/account/logout';

            throw new Error('Failed to retrieve access token');
        }
    })();

    promise.finally(() => {
        promise = null;
    });

    return promise;
};

export const refreshToken = async () => {
    try {
        const response = await fetch(`/auth/token?refresh=true`, { credentials: 'same-origin' });
        if (response.status !== 200) {
            throw new Error(`Token refresh returned ${response.status}`);
        }

        // Store the access token
        token = await response.json();

        if (!token?.accessToken) {
            throw new Error('No access token received');
        }
    } catch (err) {
        console.log(err);
    }
};

export async function getAuthHeaders(): Promise<AuthHeaderResponse> {
    // we are using the cookie to determine if the user is a guest.
    // the observable relies on the token and folder key in the url
    // if the user refreshes any page without those query params,
    // the app won't know it is guest unless we check for a
    // cookie. Who wants a cookie, me want cookie (<...
    let isGuest = false;
    let accessToken = getShareAccessToken();

    if (accessToken) {
        isGuest = true;
        isGuestUser$.next(true);
    } else {
        accessToken = await getAccessToken();
    }

    try {
        if (!accessToken) {
            throw new Error('Failed to retrieve access token');
        }

        if (isGuest) {
            return {
                Authorization: `ShareToken ${accessToken}`,
            };
        }

        return { 
            Authorization: `Bearer ${accessToken}`
        };
    } catch (error) {
        console.error('Error in getAuthHeaders:', error);
        throw error;
    }
}
